import {
  BookOpenText,
  Briefcase,
  ClockCounterClockwise,
  Files,
  FunnelSimple,
  GraduationCap,
  IdentificationCard,
  ListStar,
  MapPin,
  NoteBlank,
  PaperPlaneTilt,
  Star,
  Tag,
  Translate,
  UserList,
} from "@phosphor-icons/react";

import { LeadSource, LinkedInAccountsTag } from "features/leads/components";

import type { Lead, LeadsSearchParams } from "common/types";

import LeadDetailTag from "./components/AllLeads/LeadTags/LeadDetailTag";
import MobileTags from "./components/AllLeads/MobileLayout/MobileTags";

import type { JSX } from "react";

export const MANAGE_LEADS_ACTIONS = {
  labels: {
    icon: Tag,
    getLabel: () => "Add labels",
    getEligibleItems: (selection: Lead[]) => selection,
    isPopup: true,
  },
  notes: {
    icon: NoteBlank,
    getLabel: () => "Add notes",
    getEligibleItems: (selection: Lead[]) => selection,
  },
  favorite: {
    icon: Star,
    getLabel: () => "Favorite leads",
    getEligibleItems: (selection: Lead[]) =>
      selection.filter((lead) => !lead.favorite),
  },
  unfavorite: {
    icon: Star,
    getLabel: () => "Unfavorite leads",
    getEligibleItems: (selection: Lead[]) =>
      selection.every((lead) => lead.favorite) ? selection : [],
  },
} as const;

type FiltersType<Keys extends keyof LeadsSearchParams<never>> = {
  [K in Keys]: {
    icon: JSX.Element;
    label: string;
    isBoolean?: true;
  };
};

export const LEADS_FILTERS: FiltersType<
  keyof Omit<LeadsSearchParams<never>, "keywords">
> = {
  favorite: {
    icon: <Star weight="fill" />,
    label: "Favorite",
    isBoolean: true,
  },
  lead_of: { icon: <UserList weight="fill" />, label: "Account" },
  origins: { icon: <FunnelSimple weight="fill" />, label: "Origin" },
  current_companies: {
    icon: <Briefcase weight="fill" />,
    label: "Current company",
  },
  past_companies: {
    icon: <ClockCounterClockwise />,
    label: "Past company",
  },
  locations: {
    icon: <MapPin weight="fill" />,
    label: "Location",
  },
  labels: {
    icon: <Tag weight="fill" />,
    label: "Label",
  },
  education: {
    icon: <GraduationCap weight="fill" />,
    label: "Education",
  },
  skills: {
    icon: <BookOpenText weight="fill" />,
    label: "Skill",
  },
  interests: { icon: <ListStar weight="fill" />, label: "Interest" },
  languages: { icon: <Translate weight="fill" />, label: "Language" },
};

export const MOBILE_TAGS: Record<
  string,
  {
    icon: JSX.Element;
    label: string;
    isDefault?: true;
    renderTag?: (lead: Lead) => JSX.Element;
  }
> = {
  occupation: {
    icon: <IdentificationCard weight="fill" />,
    label: "Occupation",
    isDefault: true,
  },
  accounts: {
    icon: <UserList weight="fill" />,
    label: "Accounts",
    isDefault: true,
    renderTag: ({ lead_of }) => (
      <LinkedInAccountsTag accountIds={lead_of} size="sm" />
    ),
  },
  origins: {
    icon: <FunnelSimple weight="fill" />,
    label: "Origins",
    renderTag: ({ id }) => <LeadSource leadId={id} variant="tagSm" />,
  },
  currentCompanies: {
    icon: <Briefcase weight="fill" />,
    label: "Current companies",
    renderTag: ({ id }) => (
      <LeadDetailTag leadId={id} leadKey="current_experience" />
    ),
  },
  pastCompanies: {
    icon: <ClockCounterClockwise weight="fill" />,
    label: "Past companies",
    renderTag: ({ id }) => (
      <LeadDetailTag leadId={id} leadKey="work_experience" />
    ),
  },
  location: {
    icon: <MapPin weight="fill" />,
    label: "Location",
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="location" />,
  },
  labels: {
    icon: <Tag weight="fill" />,
    label: "Labels",
    renderTag: ({ labels }) => <MobileTags labels={labels} />,
  },
  education: {
    icon: <GraduationCap weight="fill" />,
    label: "Education",
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="education" />,
  },
  skills: {
    icon: <BookOpenText weight="fill" />,
    label: "Skills",
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="skills" />,
  },
  interests: {
    icon: <ListStar weight="fill" />,
    label: "Interests",
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="interests" />,
  },
  languages: {
    icon: <Translate weight="fill" />,
    label: "Languages",
    renderTag: ({ id }) => <LeadDetailTag leadId={id} leadKey="languages" />,
  },
} as const;

export const OUTREACH_CRM_EXPORT_OPTIONS = {
  campaign: {
    icon: PaperPlaneTilt,
  },
  list: {
    icon: Files,
  },
};

export const LEADS_TAB_ITEMS = [
  { label: "Leads", path: "" },
  { label: "Labels", path: "labels" },
];
